import { ApiError, apiRequest } from './client';
import { createSingleFlightQueue, type SingleFlightQueue } from './sync';
import type { StructuralDelta } from '../app/structuralDelta';
import type { DocId, NodeId, TagId } from '../lib/types';

/**
 * The structural resync: `PUT /api/map`, and the guard around it.
 *
 * WHAT "STRUCTURAL" MEANS. This payload touches only structural columns — position,
 * hierarchy, colour, doc links, tag assignment — never translations and never document
 * content, and every id in it already exists server-side (create and delete have their own
 * endpoints).
 *
 * IT IS A DELTA NOW (V0.34), not the whole map. It used to be the whole map on every save,
 * which was safe by construction and easy to reason about, and which is also what made two
 * people in the app at once fight: every save bumped `dataVersion` for everybody, and a
 * slightly stale full snapshot silently deleted work its sender had never seen. What travels
 * now is what changed — `app/structuralDelta.ts` decides that and owns the shape; this file
 * only carries it. A body without `mode:'delta'` is refused by the server as a stale client.
 *
 * TWO THINGS ARE DELIBERATELY NOT HERE.
 *
 * The payload BUILDER is not here. It needs the map's own view of itself, and the baseline it
 * is diffed against; `createStructuralSyncQueue` takes a runner that produces the body at
 * flush time instead, which is not merely decoupling: the trailing run must send state as it
 * is WHEN IT RUNS, because more edits are exactly what caused the overlap that owed it.
 *
 * The 800 ms debounce is not here either. "Has this edit finished being made" and "is there
 * already a request in flight" are two different questions, and the vanilla script answers
 * only the first. Keeping them apart is what lets the map commit a drag immediately on
 * `pointerup` while still debouncing keystroke-driven edits.
 */

/** One node, as the structural payload describes it. `collapsed` is absent on purpose:
 *  branch expansion is personal, per-user view state and is never synced to anyone else. */
export interface StructuralNode {
  x: number | null;
  y: number | null;
  side: number | null;
  depth: number | null;
  color: string | null;
  root: boolean;
  parentId: NodeId | null;
  docId: DocId | null;
  internalDocId: DocId | null;
  courseDocId: DocId | null;
  demoDocId: DocId | null;
}

export interface StructuralTag {
  color: string | null;
  /** Tag collapse IS shared: it is tag structure, not a personal view. One person collapsing
   *  a group collapses it for everybody, which is the documented behaviour. */
  collapsed: boolean;
  sortOrder: number;
  parentTagId: TagId | null;
  categoryId: string | null;
  invisible: boolean;
}

export interface StructuralSyncResult {
  dataVersion: number;
  /**
   * Did anything actually move?
   *
   * The server detects a no-op by read-compare-write and leaves `mapMeta` alone when nothing
   * differed, so `changed:false` comes back with the version unchanged — and nobody else is
   * told to refetch. A client that reaches this is one whose delta was already true.
   */
  changed: boolean;
}

/** The transport. Throws `ApiError` on anything that is not a 2xx, 409 included. */
export function putStructuralMap(body: StructuralDelta): Promise<StructuralSyncResult> {
  return apiRequest<StructuralSyncResult>('/api/map', { method: 'PUT', body });
}

/**
 * "Is my copy still current?" — one row, one column (V0.34).
 *
 * The fallback half of the live-update story: the presence socket carries `mapChanged` for
 * everyone who has `SHOW_PRESENCE`, and this is what every other tab — and any tab whose
 * socket quietly dropped — asks on focus instead. Deliberately NOT a reuse of `GET /api/map`,
 * which runs eight Prisma queries and serialises the whole graph; this has to be near-free
 * because every open tab calls it on every focus and visibility change.
 */
export const getMapVersion = (): Promise<{ dataVersion: number }> =>
  apiRequest<{ dataVersion: number }>('/api/map/version', { cache: 'no-store' });

/**
 * Did the map change underneath us?
 *
 * Its own predicate because the caller's response is completely different from every other
 * failure: a 409 is not a fault to report, it is somebody else having saved first, and the
 * only honest answer is to take their version rather than to overwrite their work.
 */
export const isMapConflict = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 409;

/**
 * The version the server says it is actually on, out of a 409 body.
 *
 * It is the refresh TARGET and nothing else. Assigning it to `S.dataVersion` would let a
 * trailing queue run re-send the change that was just rejected, on top of the work that
 * rejected it — see the conflict note in `app/mapWrites.ts`.
 */
export function conflictVersion(err: unknown): number | null {
  if (!(err instanceof ApiError)) return null;
  const v = (err.body as { currentDataVersion?: unknown } | undefined)?.currentDataVersion;
  return typeof v === 'number' ? v : null;
}

/**
 * What a flush needs from whoever owns the map. Three callbacks, one per outcome.
 *
 * `build` returning `null` is a first-class answer, not an error: an empty delta must cost NO
 * REQUEST AT ALL. That is what makes every remaining accidental `save()` free, and what stops
 * one client's no-op bumping everyone else's `dataVersion` — the whole point of the release.
 */
export interface StructuralSyncRunner {
  /** Build the delta to send, at FLUSH time. `null` means nothing is owed. */
  build: () => StructuralDelta | null;
  /** A 200. The baseline advances here, and only here. */
  commit: (result: StructuralSyncResult, sent: StructuralDelta) => void;
  /** The request failed — 409 included. Whatever `build` drained has to be re-armed. */
  rollback: (err: unknown, sent: StructuralDelta) => void;
}

/**
 * A single-flight `PUT /api/map` over a runner.
 *
 * The queue's own contract is unchanged (`api/sync.ts`): one run at a time, any number of
 * overlapping requests collapsing into exactly one trailing run, and the body built when the
 * run happens rather than when it was asked for. What moved is that the run now has three
 * outcomes rather than one — nothing to send, sent and accepted, sent and refused — because
 * the baseline the next diff is taken against may only advance on the middle one.
 */
export function createStructuralSyncQueue(runner: StructuralSyncRunner): SingleFlightQueue {
  return createSingleFlightQueue(async () => {
    const body = runner.build();
    if (!body) return;
    let result: StructuralSyncResult;
    try {
      result = await putStructuralMap(body);
    } catch (err) {
      runner.rollback(err, body);
      throw err;
    }
    runner.commit(result, body);
  });
}
